import React from 'react';
import Badge from '../core/Badge/Badge';
import {
  Calendar, Clock, Video, MapPin, FileText, User, X,
  CheckCircle, XCircle, ExternalLink
} from 'lucide-react';
import { formatDate } from '../../utils/formatters';
import './SessionQuickViewModal.scss';

interface Session {
  id: string;
  title: string;
  description?: string;
  scheduled_date: string;
  scheduled_time?: string;
  duration_minutes: number | null;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  meeting_link?: string;
  location?: string;
  mentee_first_name?: string;
  mentee_last_name?: string;
  mentor_first_name?: string;
  mentor_last_name?: string;
}

interface SessionQuickViewModalProps {
  session: Session;
  onClose: () => void;
  onViewDetails: () => void;
  role: 'mentor' | 'mentee';
}

const SessionQuickViewModal: React.FC<SessionQuickViewModalProps> = ({
  session,
  onClose,
  onViewDetails,
  role
}) => {
  const getStatusBadge = (status: string) => {
    const variants: Record<string, any> = {
      scheduled: { variant: 'info', icon: <Clock size={14} />, label: 'Programada' },
      in_progress: { variant: 'warning', icon: <Video size={14} />, label: 'En progreso' },
      completed: { variant: 'success', icon: <CheckCircle size={14} />, label: 'Completada' },
      cancelled: { variant: 'danger', icon: <XCircle size={14} />, label: 'Cancelada' }
    };

    const config = variants[status] || { variant: 'default', label: status };
    return (
      <Badge variant={config.variant}>
        {config.icon}
        {config.label}
      </Badge>
    );
  };

  const getParticipantName = () => {
    if (role === 'mentor') {
      return session.mentee_first_name && session.mentee_last_name
        ? `${session.mentee_first_name} ${session.mentee_last_name}`
        : 'Mentee';
    } else {
      return session.mentor_first_name && session.mentor_last_name
        ? `Con ${session.mentor_first_name} ${session.mentor_last_name}`
        : 'Mentor';
    }
  };

  return (
    <div className="session-quick-view-overlay" onClick={onClose}>
      <div className="session-quick-view-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header__content">
            <h2>{session.title}</h2>
            {getStatusBadge(session.status)}
          </div>
          <button className="modal-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {/* Participant Info */}
          <div className="quick-info-item">
            <div className="quick-info-icon">
              <User size={18} />
            </div>
            <div className="quick-info-content">
              <span className="quick-info-label">
                {role === 'mentor' ? 'Mentee' : 'Mentor'}
              </span>
              <span className="quick-info-value">{getParticipantName()}</span>
            </div>
          </div>

          {/* Date and Time */}
          <div className="quick-info-item">
            <div className="quick-info-icon">
              <Calendar size={18} />
            </div>
            <div className="quick-info-content">
              <span className="quick-info-label">Fecha y hora</span>
              <span className="quick-info-value">
                {formatDate(session.scheduled_date)}
                {session.scheduled_time && ` • ${session.scheduled_time}`}
              </span>
            </div>
          </div>

          {/* Duration */}
          <div className="quick-info-item">
            <div className="quick-info-icon">
              <Clock size={18} />
            </div>
            <div className="quick-info-content">
              <span className="quick-info-label">Duración</span>
              <span className="quick-info-value">{session.duration_minutes || 60} minutos</span>
            </div>
          </div>

          {/* Description */}
          {session.description && (
            <div className="quick-info-item quick-info-item--full">
              <div className="quick-info-icon">
                <FileText size={18} />
              </div>
              <div className="quick-info-content">
                <span className="quick-info-label">Descripción</span>
                <p className="quick-info-description">{session.description}</p>
              </div>
            </div>
          )}

          {/* Meeting Link */}
          {session.meeting_link && (
            <div className="quick-info-item">
              <div className="quick-info-icon">
                <Video size={18} />
              </div>
              <div className="quick-info-content">
                <span className="quick-info-label">Reunión virtual</span>
                <a
                  href={session.meeting_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="meeting-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  Unirse a la videollamada
                  <ExternalLink size={14} />
                </a>
              </div>
            </div>
          )}

          {/* Location */}
          {session.location && (
            <div className="quick-info-item">
              <div className="quick-info-icon">
                <MapPin size={18} />
              </div>
              <div className="quick-info-content">
                <span className="quick-info-label">Ubicación</span>
                <span className="quick-info-value">{session.location}</span>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn--outline" onClick={onClose}>
            Cerrar
          </button>
          <button className="btn btn--primary" onClick={onViewDetails}>
            Ver detalles completos
            <ExternalLink size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionQuickViewModal;
